import { useCallback, useRef } from 'react'

import { skipAbortError } from '@app/errors/AbortError'

import { useAppDispatch } from '@app/utils/redux'

import { getProgress } from '@app/store/actions/contexts/progress'

/** A ref that focuses its element once it can take focus; `autoFocus` is lost in a dialog still opening or under a blocking request. */
export const useInitialFocusRef = () => {
  const dispatch = useAppDispatch()
  const controllerRef = useRef<AbortController | null>(null)

  return useCallback(
    (el: HTMLElement | null) => {
      controllerRef.current?.abort()
      controllerRef.current = null
      if (!el) return
      const controller = new AbortController()
      controllerRef.current = controller
      void (async () => {
        await whenDialogOpen(el, controller.signal)
        await dispatch(getProgress()).whenUnblocked(controller.signal)
        el.focus()
      })().catch(skipAbortError)
    },
    [dispatch]
  )
}

const whenDialogOpen = (el: HTMLElement, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const dialog = el.closest('dialog')
    if (!dialog || dialog.open) {
      resolve()
      return
    }
    const observer = new MutationObserver(() => {
      if (!dialog.open) return
      observer.disconnect()
      resolve()
    })
    observer.observe(dialog, { attributes: true, attributeFilter: ['open'] })
    signal.addEventListener(
      'abort',
      () => {
        observer.disconnect()
        reject(signal.reason)
      },
      { once: true }
    )
  })
